import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
} from "@angular/core";
import { BaseProductDetailComponent } from "@components/products/base-product-detail.component";
import { FundListingDisplayComponent } from "../fund-listing-display-component.interface";
import { AddFundsModal } from "@products/types/add-funds.type";
import { CommonService } from "@services/common.service";
import {
  FundListingBRConfiguration,
  FundListingComponentType,
  FundListingState,
  FundListingDataFormat,
  FundId,
} from "@types";
import { Subscription, combineLatest } from "rxjs";
import {
  FundCompareDetail,
} from "../fund-compare/fund-compare.type";
import { FundCompareService } from "../services/fund-compare.service";
import { FundListingService } from "../services/fund-listing.service";
import { TranslateService } from "@components/shared/translate/translate.service";
import { WatchListService } from "../services/watch-list.service";
import { Logger } from "@utils/logger";
import { WatchListState } from "@components/products/types/watch-list.type";
import { Location } from "@angular/common";

const logger = Logger.getLogger("HomeFundListingComponent");

@Component({
  selector: "ft-fund-watch-list",
  templateUrl: "./fund-watch-list.component.html",
  styleUrls: ["./fund-watch-list.component.scss"],
})
export class FundWatchListComponent
  extends BaseProductDetailComponent
  implements FundListingDisplayComponent, OnInit, OnDestroy {
  brConfiguration: FundListingBRConfiguration;
  dataSubscription: Subscription;
  componentType: FundListingComponentType;
  dataCardToggle: boolean = true;
  funds: any = []; // please maintain model don't use any
  fund: any;

  showWatchlist: boolean = false;
  watchListingState: WatchListState;
  watchListingData: FundListingDataFormat[] | FundListingDataFormat[];
  isLoaded: boolean;

  // Fund comparison declarations.
  fundCompareIds: FundId[] = [];
  fundCompareDetails: FundCompareDetail[] = [];
  showCompareDetails: boolean; // To minimize/maximize the sticky footer window.

  fundListingState: FundListingState;
  fundData: FundListingDataFormat[] | FundListingDataFormat[] = [];
  sideFilterOptions: any;
  filterName: string;
  addFundsData: AddFundsModal;

  @Input() isAddFund = false;
  @ViewChild("toastScrollPoint") toastScrollPointRef: ElementRef;

  constructor(
    private cdr: ChangeDetectorRef,
    private fundCompareService: FundCompareService,
    private fundListingService: FundListingService,
    private commonService: CommonService,
    private watchListService: WatchListService,
    private translateService: TranslateService,
    private location: Location
  ) {
    super();
  }

  ngOnInit() {
    super.ngOnInit();

    this.brConfiguration = {
      config: {
        componentType: FundListingComponentType.PPSS,
        assetCategory: "All",
      },
      funds: this.component?.getModels()?.funds?.funds,
    };
    this.addFundsData = {
      fundList: [],
      assetClassFilter: {
        filterLabel: this.translateService.instant(
          "products.add-fund-filter-label"
        ),
        filterName: this.translateService.instant(
          "products.add-fund-filter-label"
        ),
        filterItems: [],
      },
      addFundsButtonlabel: this.translateService.instant(
        "products.add-fund-button-label"
      ),
      modalTitle: this.translateService.instant(
        "products.add-fund-modal-title"
      ),
      submitLabel: this.translateService.instant(
        "products.add-fund-submit-label"
      ),
      resetlabel: this.translateService.instant(
        "products.add-fund-reset-label"
      ),
      maxLimitExceedMessage: this.translateService.instant(
        "products.add-fund-watch-list-max-limit-message"
      ),
      searchInput: {
        label: this.translateService.instant(
          "products.add-fund-search-input-label"
        ),
        name: this.translateService.instant(
          "products.add-fund-search-input-label"
        ),
        placeholder: this.translateService.instant(
          "products.add-fund-search-input-placeholder"
        ),
      },
      selectedFundIds: [],
      maxLimit: 9,
    };
    this.initalizeWatchlist();

    this.watchListService.watchlist$.subscribe((funds) => {
      this.watchListingData = funds;
      this.cdr.detectChanges();
    });
  }

  /**
   * Initialized when current commponent gets loaded.
   */
  initalizeWatchlist(): void {
    this.watchListService.setWatchListLimit(9);
    this.componentType = FundListingComponentType.PPSS;
  }

  /**
   * Initialized when current commponent gets loaded.
   * @param brConfiguration bloomreach configuration received
   */
  populate(brConfiguration: FundListingBRConfiguration): void {
    // Save BR configuration only once.
    if (!this.brConfiguration) {
      this.brConfiguration = brConfiguration;
      this.brConfiguration.config.componentType = FundListingComponentType.PPSS;
    }
    this.dataSubscription = combineLatest([
      this.watchListService.getWatchListState$(),
      this.fundListingService.getFundInformationState$()
    ]).subscribe(
      async (state) => {
        let watchListState: any;
        let allFund: any;
        [watchListState, allFund] = state;
        logger.debug("Mapped fund listing state: ", state);
        this.isLoaded = watchListState.isLoaded || allFund.isLoaded;
        if (this.isLoaded) {
          // Assign received state for specific component type.
          this.watchListingState = watchListState;
          this.addFundsData.selectedFundIds = watchListState?.selectedFundIds;
          this.addFundsData.fundList = watchListState?.addFundList;
          this.addFundsData.assetClassFilter =
            watchListState.assetClassFilterDropdown;
          this.addFundsData.selectedFundIds = watchListState.selectedFundIds;
          // Check if, fund ids exist in local storage.
          // If yes, pass the list information to the fund card and sticky footer accordingly.
          // ***** To be placed here, before the listing object 'fundListingData' gets populated. *****
          this.componentType = this.brConfiguration.config.componentType;
          this.fundListingState = allFund;
          // side Options
          this.sideFilterOptions = allFund.filterDropdowns[0].filterItems.map(
            (fund, index) => ({
              id: (index + 1).toString(),
              name: fund.label,
            })
          );
          this.fundData = this.fundListingState?.ppssState?.ppssData
            ? JSON.parse(
              JSON.stringify(this.fundListingState?.ppssState?.ppssData)
            )
            : [];
          try {
            const storedFundCompareDetails: FundCompareDetail[] =
              await this.fundCompareService.retrieveFundDetailListFromLocalStorage();

            // Only save the valid list, else remain it initialized with an empty array.
            if (storedFundCompareDetails?.length) {
              this.fundCompareDetails = storedFundCompareDetails;
            }

            // Also, show sticky footer panel in minimized state for the first load, when fund ids present in local storage.
            // Skip this, when state is refreshed through filter applied.
            if (
              this.fundCompareDetails.length &&
              this.showCompareDetails === undefined
            ) {
              this.showCompareDetails = false;
            }
            // Additionally, extract the fund ids and the fund names from the retrieved list
            // to pass the ids to the fund card and to show the sticky panel respectively.
            // But, first, reset the list for every new filter state, and, let it have the fresh list from 'fundCompareDetails'.
            this.fundCompareIds = [];
            this.fundCompareDetails.forEach((fundCompareDetail) => {
              this.fundCompareIds.push(fundCompareDetail.fundId);
            });
          } catch (error) {
            logger.error("Error in communicating with local storage: ", error);
          }
          this.watchListService.watchlist$.subscribe((funds) => {
            this.watchListingData = funds;
            this.cdr.detectChanges();
          });

        }
        this.commonService.send_added_fund.emit(true);
        this.cdr.detectChanges();
      },
      (error) => {
        logger.error("Error in invoking fund listing service: ", error);

        this.isLoaded = false;
        this.cdr.detectChanges();
      }
    );

    this.watchListService.populate(this.brConfiguration);
  }

  onToggleDataCard(value: boolean) {
    this.dataCardToggle = value;
  }

  backClicked() {
    this.location.back();
  }

  ngOnDestroy(): void {
    // Clean up subscription to avoid memory leaks.
    this.dataSubscription?.unsubscribe();
  }
}
